import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Archive,
  ArrowLeftRight,
  Brain,
  Briefcase,
  BriefcaseBusiness,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarPlus2,
  ChartGantt,
  ChartLine,
  ChartNoAxesColumn,
  ClipboardCheck,
  ClipboardList,
  Clock3,
  ContactRound,
  FileText,
  FolderOpen,
  HandCoins,
  ListChecks,
  Mail,
  Megaphone,
  Menu,
  PackageCheck,
  PieChart,
  ReceiptText,
  Settings,
  SlidersHorizontal,
  Sparkles,
  X,
  Search,
  History,
  LayoutGrid,
  Target,
  Timer,
  UserCheck,
  UserMinus,
  UserPlus,
  UserRoundSearch,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ProfileIcon from "../ProfileIcon/ProfileIcon";
import ResponsiveNav from "./ResponsiveNav";
import PunchIn from "./PunchIn";
import NotificationCenter from "../Notifications/NotificationCenter";
import "./Navbar.scss";
import DefaultProfileIcon from "../../Images/user-1.jpg";
import axiosInstance from "../../axiosInstance";
import { SIDEBAR_SECTIONS, canSee, isItemActive } from "../Sidebar/sidebarMenu";

const NAVIGATION_ICONS = {
  Dashboard: LayoutGrid,
  Leave: CalendarDays,
  "My Leave": CalendarCheck2,
  "Apply Leave": CalendarPlus2,
  Fallback: ArrowLeftRight,
  Attendance: Clock3,
  "My Logs": ListChecks,
  "My Time Sheet": Timer,
  Reimbursements: ReceiptText,
  "Emergency Funds": HandCoins,
  Payroll: HandCoins,
  "List Of Holidays": CalendarDays,
  Policy: FileText,
  "Mark Attendance": ClipboardCheck,
  "Manage Leave": CalendarCheck2,
  "Manage Fallback": ArrowLeftRight,
  "Manage Attendance": UserCheck,
  "Time Sheet": Timer,
  "Loan Approval": HandCoins,
  Candidates: UserRoundSearch,
  Vendors: Building2,
  Recruitments: BriefcaseBusiness,
  Jobs: Briefcase,
  Walkin: ContactRound,
  Timeline: ChartGantt,
  "On-Boarding Repository": Archive,
  "Check Lists": ListChecks,
  "Document Checklist": FileText,
  "Assets Checklist": PackageCheck,
  "HR Checklist": ClipboardCheck,
  "Assign Mentor": UsersRound,
  Repository: FolderOpen,
  "My Mindset Report": Brain,
  "Review Parameters": SlidersHorizontal,
  "Goals / KRA Assessment Master": Target,
  "Skills Assessment Master": Sparkles,
  Appraisal: ClipboardCheck,
  "My Assessments": FileText,
  "Team Assessments": UsersRound,
  "Appraisal Report": ChartLine,
  "Performance Report": ChartNoAxesColumn,
  "Off-Boarding": UserMinus,
  "Off-Board Check List": ClipboardCheck,
  "Apply Resignation": UserMinus,
  "Resignation Applicants": UserRoundSearch,
  "Payroll Report": PieChart,
  "Salary Register": ClipboardList,
  "Salary Budget": Settings,
  "Payroll Summary": ChartNoAxesColumn,
  Announcement: Megaphone,
  "Time & Attendance": Clock3,
  "Leave Management": CalendarDays,
  "Custom/General Reminders": Settings,
  "Employee Updates": Mail,
  CRM: UsersRound,
  "Recruitment (Company)": Building2,
  "Recruitment (Consultancy)": BriefcaseBusiness,
};

const getNavigationIcon = (name = "") => {
  if (NAVIGATION_ICONS[name]) return NAVIGATION_ICONS[name];
  const normalized = name.toLowerCase();
  if (normalized.includes("leave")) return CalendarDays;
  if (normalized.includes("attendance")) return Clock3;
  if (normalized.includes("payroll")) return HandCoins;
  if (normalized.includes("recruit")) return Briefcase;
  if (normalized.includes("report")) return ChartLine;
  if (normalized.includes("reminder") || normalized.includes("notification")) {
    return Settings;
  }
  return LayoutGrid;
};

// --- "WHAT'S NEW" UPDATES CONFIG ---
// Single source of truth for the updates list shown in the dropdown.
// `LATEST_UPDATE_VERSION` should match the version of the newest item below.
// Bump this whenever a new update is shipped so returning users see the
// red indicator again.
const UPDATES_LIST = [
  {
    version: "v2",
    description:
      "Major workspace refresh: navbar-led module navigation, compact and expanded sidebar views, a first-run navigation tour, and more consistent page layouts.",
  },
  {
    version: "v1.1.1",
    description:
      "Fixed attendance calendar issues with Weekly Off days and employee Date of Joining (DOJ) based attendance data.",
  },
  {
    version: "v1.1.0",
    description:
      "A refreshed table experience, a cleaner interface,collapse sidebar with submenus and a more polished, enterprise-ready feel throughout the app.",
  },
];

const LATEST_UPDATE_VERSION = UPDATES_LIST[0].version;
// sessionStorage key - only ever stores a plain version string (e.g. "v2.3.0").
// No user data, tokens, or sensitive information is stored here.
const SEEN_UPDATE_STORAGE_KEY = "hrlabs_last_seen_update_version";

// Pages reachable from the global search box. (Search is page navigation
// only - there is no employee/record search API behind it yet.)
const getSearchItems = (roles) => {
  const items = [];
  const visit = (entry) => {
    if (!canSee(entry, roles)) return;
    items.push({ name: entry.name, route: entry.to });
    (entry.submenu || []).forEach(visit);
  };
  SIDEBAR_SECTIONS.forEach((section) => section.items.forEach(visit));
  return [...new Map(items.map((item) => [item.route, item])).values()];
};

const SHORTCUT_LABEL =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/i.test(navigator.platform || "")
    ? "⌘K"
    : "Ctrl K";

const formatRole = (role = "") =>
  role
    .replace("ROLE_", "")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Props
 *  - onNotificationReceived : passed through to NotificationCenter
 *  - onMenuToggle           : toggles the mobile sidebar drawer (AppLayout)
 *  - isMobileNavOpen        : drawer state, for the hamburger's aria/icon
 */
const Navbar = ({
  onNotificationReceived,
  onMenuToggle,
  isMobileNavOpen = false,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navbarRef = useRef(null);
  const searchInputRef = useRef(null);

  // Which header dropdown is open: "search" | "updates" | "quick" | "profile"
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasNewUpdate, setHasNewUpdate] = useState(false);
  const [emailCategories, setEmailCategories] = useState([]);
  const [selectedEmailCategoryId, setSelectedEmailCategoryId] = useState(null);
  const isEmailAutomationRoute = pathname === "/email-automation";
  const isComingSoonRoute =
    pathname === "/recruitment" ||
    pathname === "/onboarding" ||
    pathname === "/on-boarding" ||
    pathname.startsWith("/performance-management/") ||
    pathname.startsWith("/offboarding/");
  const authorizedModules = SIDEBAR_SECTIONS.flatMap(
    (section) => section.items,
  ).filter(
    (item) =>
      canSee(item, user?.authorities || []) && (item.submenu || []).length,
  );
  const activeModule = isComingSoonRoute
    ? null
    : authorizedModules.find((item) => isItemActive(item, pathname));
  const hasNavbarContext = Boolean(
    activeModule || (isEmailAutomationRoute && emailCategories.length),
  );
  const activeSection = activeModule?.submenu
    ?.filter((item) => canSee(item, user?.authorities || []))
    .find((item) => isItemActive(item, pathname));
  const activeSubtabs = (activeSection?.submenu || []).filter((item) =>
    canSee(item, user?.authorities || []),
  );
  const contextNavigationItems = isEmailAutomationRoute
    ? emailCategories.map((category) => ({
        key: category.categoryId,
        label: category.categoryName,
        icon: getNavigationIcon(category.categoryName),
        active: selectedEmailCategoryId === category.categoryId,
        onSelect: () => {
          setSelectedEmailCategoryId(category.categoryId);
          window.dispatchEvent(
            new CustomEvent("email-automation-category-select", {
              detail: category.categoryId,
            }),
          );
        },
      }))
    : (activeModule?.submenu || [])
        .filter((item) => canSee(item, user?.authorities || []))
        .map((item) => ({
          key: item.to,
          label: item.name,
          to: item.to,
          icon: getNavigationIcon(item.name),
          active: isItemActive(item, pathname),
        }));
  const subtabNavigationItems = activeSubtabs.map((item) => ({
    key: item.to,
    label: item.name,
    to: item.to,
    icon: getNavigationIcon(item.name),
    active: isItemActive(item, pathname),
  }));

  useEffect(() => {
    if (
      !isEmailAutomationRoute ||
      !canSee({ roles: ["ROLE_ADMIN", "ROLE_HR"] }, user?.authorities || [])
    ) {
      setEmailCategories([]);
      setSelectedEmailCategoryId(null);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get(`${process.env.REACT_APP_API_EMPLOYEE_URL}/automation-category/all`)
      .then(({ data }) => {
        if (cancelled) return;
        const categories = data.data || [];
        setEmailCategories(categories);
        setSelectedEmailCategoryId((currentId) =>
          categories.some((category) => category.categoryId === currentId)
            ? currentId
            : categories[0]?.categoryId || null,
        );
      })
      .catch(() => {
        if (!cancelled) setEmailCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isEmailAutomationRoute, user]);

  useEffect(() => {
    const header = navbarRef.current;
    if (!header) return undefined;
    const syncHeight = () => {
      if (!hasNavbarContext) {
        document.documentElement.style.removeProperty("--navbar-height");
        document.documentElement.style.removeProperty(
          "--navbar-context-height",
        );
        return;
      }
      const mainRow = header.querySelector(".navbar-left");
      const minMainRowHeight =
        Number.parseFloat(window.getComputedStyle(header).minHeight) || 0;
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${header.getBoundingClientRect().height}px`,
      );
      if (mainRow) {
        document.documentElement.style.setProperty(
          "--navbar-context-height",
          `${Math.max(mainRow.getBoundingClientRect().height, minMainRowHeight)}px`,
        );
      }
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [activeModule, activeSection, activeSubtabs.length, hasNavbarContext]);

  // On mount, compare the latest shipped version against the version the
  // user last acknowledged. try/catch because storage can be blocked.
  useEffect(() => {
    try {
      const lastSeenVersion = window.sessionStorage.getItem(
        SEEN_UPDATE_STORAGE_KEY,
      );
      setHasNewUpdate(lastSeenVersion !== LATEST_UPDATE_VERSION);
    } catch (err) {
      console.warn("Unable to read update status from sessionStorage:", err);
      setHasNewUpdate(false);
    }
  }, []);

  // Close dropdowns on outside click or Escape.
  useEffect(() => {
    if (!openMenu) return undefined;
    const onMouseDown = (e) => {
      if (!e.target.closest("[data-menu-host]")) setOpenMenu(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  // Cmd/Ctrl + K focuses the search box.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenMenu("search");
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggleMenu = (name) =>
    setOpenMenu((current) => (current === name ? null : name));

  // Opening the updates dropdown marks the latest update as "seen".
  const handleUpdatesToggle = () => {
    const willOpen = openMenu !== "updates";
    toggleMenu("updates");
    if (willOpen && hasNewUpdate) {
      setHasNewUpdate(false);
      try {
        window.sessionStorage.setItem(
          SEEN_UPDATE_STORAGE_KEY,
          LATEST_UPDATE_VERSION,
        );
      } catch (err) {
        console.warn("Unable to save update status to sessionStorage:", err);
      }
    }
  };

  const handleViewAllUpdates = (e) => {
    e.preventDefault();
    setOpenMenu(null);
    navigate("/updates");
  };

  // --- DERIVED DATA from AuthContext ---
  const profileImageSrc =
    user?.profileImage && user?.profileImageType
      ? `data:${user.profileImageType};base64,${user.profileImage}`
      : DefaultProfileIcon;
  const employeeName = user?.employeeName || "Employee";
  const role = user?.role || "No Role Assigned";
  const authorities = user?.authorities || [];
  const roleLabel = formatRole(role);

  const authorizedSearchItems = getSearchItems(user?.authorities || []);
  const filteredSearchItems = authorizedSearchItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
  );

  const closeSearch = () => {
    setSearchQuery("");
    setOpenMenu(null);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && filteredSearchItems.length > 0) {
      navigate(filteredSearchItems[0].route);
      closeSearch();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
    }
  };

  const handleNotificationNavigation = (section) => {
    switch (section) {
      case "teams":
        navigate("/team/manage-team-leave");
        break;
      case "ess":
        navigate("/ess/my-logs");
        break;
      case "compensation":
        navigate("/compensation-benefits/payroll-report");
        break;
      case "emailAutomation":
        navigate("/email-automation");
        break;
      default:
        navigate(`/${section}`);
    }
  };

  const handleLogout = () => {
    setOpenMenu(null);
    logout();
  };

  // If the user is not logged in, don't render the navbar.
  if (!user) {
    return null;
  }

  return (
    <header
      className={`navbar${hasNavbarContext ? " has-context" : ""}${activeSubtabs.length ? " has-subtabs" : ""}`}
      ref={navbarRef}
    >
      {/* ---------------- Left: menu button, logo, company ---------------- */}
      <div className="navbar-left">
        <button
          type="button"
          className="navbar-menu-btn"
          onClick={onMenuToggle}
          aria-label={isMobileNavOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={isMobileNavOpen}
          aria-controls="app-sidebar"
        >
          {isMobileNavOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        {hasNavbarContext && (
          <ResponsiveNav
            items={contextNavigationItems}
            className="navbar-context"
            ariaLabel={
              isEmailAutomationRoute
                ? "Email automation modules"
                : `${activeModule.name} navigation`
            }
          />
        )}
      </div>

      {/* ---------------- Right: actions ---------------- */}
      <div className="navbar-right">
        <div className="search-container" data-menu-host>
          <button
            type="button"
            className="nav-button search-trigger"
            aria-label="Search pages"
            aria-expanded={openMenu === "search"}
            onClick={() => {
              toggleMenu("search");
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
          >
            <Search size={19} aria-hidden="true" />
          </button>
          {openMenu === "search" && (
            <div className="search-popup">
              <div className="search-field">
                <Search size={17} aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  autoFocus
                  type="search"
                  placeholder="Search pages and modules..."
                  aria-label="Search pages and modules"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <kbd>{SHORTCUT_LABEL}</kbd>
              </div>
              <div
                className="filtered-menu"
                role="listbox"
                aria-label="Authorized pages"
              >
                {filteredSearchItems.length ? (
                  filteredSearchItems.map((item) => (
                    <Link
                      key={item.route}
                      to={item.route}
                      className="filtered-item"
                      onClick={closeSearch}
                    >
                      {item.name}
                    </Link>
                  ))
                ) : (
                  <div className="filtered-empty">No matching pages</div>
                )}
              </div>
            </div>
          )}
        </div>

        <NotificationCenter
          onNavigate={handleNotificationNavigation}
          onNotificationReceived={onNotificationReceived}
        />

        {/* What's new */}
        <div className="nav-menu-host updates-container" data-menu-host>
          <button
            type="button"
            className="updates-button nav-button"
            aria-label="What's New"
            aria-haspopup="true"
            aria-expanded={openMenu === "updates"}
            onClick={handleUpdatesToggle}
          >
            <History size={19} aria-hidden="true" />
            {hasNewUpdate && <span className="update-dot" />}
          </button>

          {openMenu === "updates" && (
            <div className="updates-menu">
              <div className="updates-header">
                <h4>What's New</h4>
              </div>

              {UPDATES_LIST.map((update) => (
                <div className="update-item" key={update.version}>
                  <div className="version">{update.version}</div>
                  <p>{update.description}</p>
                </div>
              ))}

              <Link
                to="/updates"
                className="view-all"
                onClick={handleViewAllUpdates}
              >
                View All Updates
              </Link>
            </div>
          )}
        </div>

        {/* Quick access */}
        <div className="nav-menu-host quick-access-container" data-menu-host>
          <button
            type="button"
            className="quick-access-button nav-button"
            aria-label="Quick Access"
            aria-haspopup="true"
            aria-expanded={openMenu === "quick"}
            onClick={() => toggleMenu("quick")}
          >
            <LayoutGrid size={19} aria-hidden="true" />
          </button>
          {openMenu === "quick" && (
            <div className="quick-access-menu">
              <Link to="/ess/apply-leave" onClick={() => setOpenMenu(null)}>
                Apply Leave
              </Link>
              <Link
                to="/ess/list-of-holidays"
                onClick={() => setOpenMenu(null)}
              >
                List of Holidays
              </Link>
              <Link to="/ess/fallback" onClick={() => setOpenMenu(null)}>
                Fallback
              </Link>
              {authorities.includes("ROLE_MANAGER") && (
                <>
                  <Link
                    to="/team/mark-attendance"
                    onClick={() => setOpenMenu(null)}
                  >
                    Mark Attendance
                  </Link>
                  <Link
                    to="/team/manage-team-leave"
                    onClick={() => setOpenMenu(null)}
                  >
                    Manage Team Leave
                  </Link>
                </>
              )}
            </div>
          )}
        </div>

        {/* Punch In/Out (unchanged behaviour) */}
        <PunchIn className="punchin-button" />

        {/* Profile menu */}
        <div className="nav-menu-host profile-menu-container" data-menu-host>
          <button
            type="button"
            className="profile-button"
            aria-label="Profile Menu"
            aria-haspopup="true"
            aria-expanded={openMenu === "profile"}
            onClick={() => toggleMenu("profile")}
          >
            <ProfileIcon
              size="small"
              src={profileImageSrc}
              alt="Profile"
              className="profile-avatar"
            />
          </button>

          {openMenu === "profile" && (
            <div className="profile-menu">
              <div className="profile-info">
                <ProfileIcon
                  size="medium"
                  src={profileImageSrc}
                  alt="Profile"
                />
                <div className="profile-details">
                  <div className="profile-name">{employeeName}</div>
                  <div className="profile-role">
                    <span className="role">{roleLabel}</span>
                  </div>
                </div>
              </div>
              <div className="profile-options">
                <Link to="/profile" onClick={() => setOpenMenu(null)}>
                  Profile
                </Link>
                {authorities.includes("ROLE_ADMIN") && (
                  <Link to="/subscriptions" onClick={() => setOpenMenu(null)}>
                    My Subscriptions
                  </Link>
                )}
                <Link to="/account-settings" onClick={() => setOpenMenu(null)}>
                  Account Settings
                </Link>
                <button
                  type="button"
                  className="logout-a"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeSubtabs.length > 0 && (
        <ResponsiveNav
          items={subtabNavigationItems}
          className="navbar-context-subtabs"
          ariaLabel={`${activeSection.name} pages`}
          chip
        />
      )}
    </header>
  );
};

export default Navbar;
